/**
 * Captcha provider configuration.
 * Priority:
 * 1. Russian language → Geetest
 * 2. *.GonkaGame configured domain domain → Turnstile
 * 3. Otherwise → reCAPTCHA
 */

import { isTurnstileToken } from './turnstile'

export type CaptchaProvider = 'recaptcha' | 'turnstile' | 'geetest' | 'none'

/**
 * Determine which captcha provider to use.
 * In dev environment, captcha is disabled entirely (Geetest rejects unlisted domains).
 * Currently forced to Geetest for all production interfaces.
 */
export const getCaptchaProvider = (): CaptchaProvider => {
    // IGRA-30: explicit build-time override for e2e runs of the production
    // bundle against the dev backend (VITE_CAPTCHA_PROVIDER=none). Unset in
    // real builds — default behaviour below is unchanged.
    const override = import.meta.env.VITE_CAPTCHA_PROVIDER as CaptchaProvider | undefined
    if (override) {
        return override
    }
    if (import.meta.env.DEV) {
        return 'none'
    }
    return 'geetest'
}


/**
 * Check if current config uses Turnstile.
 */
export const isTurnstileDomain = (): boolean => getCaptchaProvider() === 'turnstile'

/**
 * Check if current config uses Geetest.
 */
export const isGeetestProvider = (): boolean => getCaptchaProvider() === 'geetest'

/**
 * Get the appropriate form field name for the captcha token.
 *
 * HK-IGRA.940: токен невидимой Turnstile едет в своём поле, какой бы
 * провайдер ни был видимым, — поэтому имя поля зависит от самого токена.
 */
export const getCaptchaFieldName = (token?: string): string => {
    if (token && isTurnstileToken(token)) {
        return 'cf-turnstile-response'
    }
    const provider = getCaptchaProvider()
    switch (provider) {
        case 'geetest':
            return 'geetest-response'
        case 'turnstile':
            return 'cf-turnstile-response'
        default:
            return 'g-recaptcha-response'
    }
}
